import * as React from 'react';

import { cva } from 'class-variance-authority';
import {
  ChevronLeft,
  ChevronRight,
  ChevronsLeft,
  ChevronsRight,
} from 'lucide-react';

import { cn } from '../../../lib/stylingUtils';
import { Button } from '../buttons/Button';
import { Select } from '../pickers/Select';

const DEFAULT_PAGE_SIZES = [
  { id: 25, label: '25 / page' },
  { id: 50, label: '50 / page' },
  { id: 100, label: '100 / page' },
  { id: 200, label: '200 / page' },
];
const DEFAULT_LIMIT = 25;

const paginationStyles = cva(
  ['flex items-center justify-between gap-4 text-sm'],
  {
    variants: {},
    defaultVariants: {},
  },
);

export interface PaginationProps {
  limit?: number;
  offset?: number;
  total?: number | null;
  pageSizes?: { id: number; label: string }[];
  defaultPageSize?: number;
  onChange?: (params: { limit: number; offset: number }) => void;
  className?: string;
  showPageButtons?: boolean;
  maxPageButtons?: number;
}

export const Pagination = ({
  limit,
  offset = 0,
  total,
  pageSizes = DEFAULT_PAGE_SIZES,
  defaultPageSize = DEFAULT_LIMIT,
  onChange,
  className,
  showPageButtons = false,
  maxPageButtons = 5,
}: PaginationProps) => {
  const currentLimit = limit ?? defaultPageSize;
  const currentPage = Math.floor(offset / currentLimit) + 1;
  const totalPages = total ? Math.ceil(total / currentLimit) : 0;

  const pageSizeOptions = React.useMemo(
    () =>
      pageSizes.map((item) => ({
        key: item.id.toString(),
        label: item.label,
      })),
    [pageSizes],
  );

  // Hide pagination if there are fewer than 25 items
  if (totalPages <= 1) {
    return null;
  }

  // Handler for page changes
  const handlePageChange = (page: number) => {
    if (page < 1 || (totalPages > 0 && page > totalPages)) return;

    onChange?.({
      limit: currentLimit,
      offset: (page - 1) * currentLimit,
    });
  };

  // Handler for page size changes
  const handlePageSizeChange = (pageSize: number | null) => {
    if (pageSize) {
      onChange?.({
        limit: pageSize,
        offset: 0, // Reset to first page when changing page size
      });
    }
  };

  // Generate page buttons to display
  const getPageButtons = () => {
    if (!showPageButtons || totalPages <= 1) {
      return (
        <div className="flex items-center gap-1 px-2">
          <span>
            Page {currentPage} of {totalPages || 1}
          </span>
        </div>
      );
    }

    // Ensure maxPageButtons is at least 5
    const actualMaxButtons = Math.max(5, maxPageButtons);

    // Calculate which page buttons to show
    let startPage = 1;
    let endPage = totalPages;

    if (totalPages > actualMaxButtons) {
      // Always show first and last page
      const middleButtons = actualMaxButtons - 2;
      const halfMiddle = Math.floor(middleButtons / 2);

      if (currentPage <= halfMiddle + 1) {
        // Near the start
        endPage = actualMaxButtons - 1;
      } else if (currentPage >= totalPages - halfMiddle) {
        // Near the end
        startPage = totalPages - actualMaxButtons + 2;
      } else {
        // In the middle
        startPage = currentPage - halfMiddle;
        endPage = currentPage + halfMiddle + (middleButtons % 2 === 0 ? 0 : 1);
      }
    }

    const pageButtons = [];

    // First page button (always shown)
    if (startPage > 1) {
      pageButtons.push(
        <Button
          key="page-1"
          variant={currentPage === 1 ? 'solid' : 'outline'}
          size="small"
          className="size-8"
          onPress={() => handlePageChange(1)}
          aria-label={`Page 1`}
        >
          1
        </Button>,
      );

      // Add ellipsis if there's a gap
      if (startPage > 2) {
        pageButtons.push(
          <span key="ellipsis-start" className="px-1">
            ...
          </span>,
        );
      }
    }

    // Middle page buttons
    for (let i = startPage; i <= endPage; i++) {
      if (i === 1 || i === totalPages) continue; // Skip first and last as they're handled separately

      pageButtons.push(
        <Button
          key={`page-${i}`}
          variant={currentPage === i ? 'solid' : 'outline'}
          size="small"
          className="size-8"
          onPress={() => handlePageChange(i)}
          aria-label={`Page ${i}`}
        >
          {i}
        </Button>,
      );
    }

    // Last page button (always shown)
    if (endPage < totalPages) {
      // Add ellipsis if there's a gap
      if (endPage < totalPages - 1) {
        pageButtons.push(
          <span key="ellipsis-end" className="px-1">
            ...
          </span>,
        );
      }

      pageButtons.push(
        <Button
          key={`page-${totalPages}`}
          variant={currentPage === totalPages ? 'solid' : 'outline'}
          size="small"
          className="size-8"
          onPress={() => handlePageChange(totalPages)}
          aria-label={`Page ${totalPages}`}
        >
          {totalPages}
        </Button>,
      );
    }

    return <div className="flex items-center gap-1 px-2">{pageButtons}</div>;
  };

  return (
    <div className={cn('@Pagination', paginationStyles())}>
      <div className="flex items-center gap-2 text-sm text-neutral/80">
        {total != null && <span>Total {total} items</span>}
      </div>

      <div className={cn('flex items-center gap-2', className)}>
        <Select
          options={pageSizeOptions}
          value={currentLimit?.toString()}
          onChange={(key) => handlePageSizeChange(Number(key))}
          className="w-30"
        />

        {/* Pagination controls */}
        <div className="flex items-center gap-1">
          <Button
            variant="outline"
            onPress={() => handlePageChange(1)}
            isDisabled={currentPage <= 1}
            icon={<ChevronsLeft />}
            aria-label="First page"
          />

          <Button
            variant="outline"
            onPress={() => handlePageChange(currentPage - 1)}
            isDisabled={currentPage <= 1}
            icon={<ChevronLeft />}
            aria-label="Previous page"
          />

          {getPageButtons()}

          <Button
            variant="outline"
            onPress={() => handlePageChange(currentPage + 1)}
            isDisabled={!totalPages || currentPage >= totalPages}
            icon={<ChevronRight />}
            aria-label="Next page"
          />

          <Button
            variant="outline"
            onPress={() => handlePageChange(totalPages)}
            isDisabled={!totalPages || currentPage >= totalPages}
            icon={<ChevronsRight />}
            aria-label="Last page"
          />
        </div>
      </div>
    </div>
  );
};
